import { toFixed } from "./helper";

export const ACCESS_TYPES = {
  ALL: "all",
  FREE: "free",
  BUY: "buy",
  PPV: "ppv",
  EXCLUSIVE: "exclusive",
};

export const ACCESS_FILTER_OPTIONS = [
  {
    id: ACCESS_TYPES.ALL,
    name: "All Content",
    label: "All Content",
    shortLabel: "All",
    description: "Browse all available content",
  },
  {
    id: ACCESS_TYPES.FREE,
    name: "Free Content",
    label: "Free Access",
    shortLabel: "Free",
    description: "Access immediately without purchase",
  },
  {
    id: ACCESS_TYPES.BUY,
    name: "One-Time Buy",
    label: "One-Time Buy",
    shortLabel: "Buy",
    description: "Buy once for lifetime personal access",
  },
  {
    id: ACCESS_TYPES.PPV,
    name: "Pay-Per-View (PPV)",
    label: "Pay-Per-View",
    shortLabel: "PPV",
    description: "Rent or pay per view/access",
  },
  {
    id: ACCESS_TYPES.EXCLUSIVE,
    name: "Exclusive (Subscribers)",
    label: "Exclusive VIP",
    shortLabel: "Exclusive",
    description: "Available only to active subscribers",
  },
];

/**
 * Classifies an item into one of the 4 access types:
 * 1. Exclusive: exclusive_content === "Yes"
 * 2. Pay-Per-View: ppv_content === "Yes"
 * 3. Free: price === 0 (or not specified)
 * 4. One-Time Buy: price > 0 (and not exclusive, not ppv)
 */
export const getItemAccessType = (item) => {
  const rawPrice = item?.price;
  const numPrice = Number(rawPrice);
  const price = isNaN(numPrice) ? 0 : numPrice;

  const isExclusive =
    String(item?.exclusive_content || "")
      .trim()
      .toLowerCase() === "yes";

  const isPpv =
    String(item?.ppv_content || "")
      .trim()
      .toLowerCase() === "yes";

  // 1. Exclusive content (Subscribers only)
  if (isExclusive) {
    return {
      id: ACCESS_TYPES.EXCLUSIVE,
      label: "Exclusive VIP",
      shortLabel: "Exclusive",
      badgeText: "EXCLUSIVE",
      subText: "Subscribers Only",
      color: "amber",
      isExclusive: true,
      isPpv: false,
      isFree: false,
      isBuy: false,
      price,
      formattedPrice: price > 0 ? `$${toFixed(price)}` : "VIP Access",
      badgeClass:
        "bg-amber-500/20 text-amber-300 border border-amber-500/40 shadow-amber-950/40",
      tagClass: "bg-amber-500/10 text-amber-400 border border-amber-500/20",
      btnText: "Subscriber Access",
    };
  }

  // 2. Pay-Per-View (PPV)
  if (isPpv) {
    return {
      id: ACCESS_TYPES.PPV,
      label: "Pay-Per-View",
      shortLabel: "PPV",
      badgeText: "PPV",
      subText: price > 0 ? `Rent for $${toFixed(price)}` : "Pay Per View",
      color: "purple",
      isExclusive: false,
      isPpv: true,
      isFree: false,
      isBuy: false,
      price,
      formattedPrice: `$${toFixed(price)}`,
      badgeClass:
        "bg-purple-500/25 text-purple-300 border border-purple-400/40 shadow-purple-950/40",
      tagClass: "bg-purple-500/10 text-purple-300 border border-purple-500/20",
      btnText: price > 0 ? `Rent PPV ($${toFixed(price)})` : "Rent PPV",
    };
  }

  // 3. Free Content (price == 0)
  if (price === 0) {
    return {
      id: ACCESS_TYPES.FREE,
      label: "Free Access",
      shortLabel: "Free",
      badgeText: "FREE",
      subText: "No Purchase Required",
      color: "emerald",
      isExclusive: false,
      isPpv: false,
      isFree: true,
      isBuy: false,
      price: 0,
      formattedPrice: "FREE",
      badgeClass:
        "bg-emerald-500/25 text-emerald-300 border border-emerald-400/40 shadow-emerald-950/40",
      tagClass: "bg-emerald-500/10 text-emerald-400 border border-emerald-500/20",
      btnText: "Access Free",
    };
  }

  // 4. One-Time Buy
  return {
    id: ACCESS_TYPES.BUY,
    label: "One-Time Buy",
    shortLabel: "Buy",
    badgeText: `$${toFixed(price)}`,
    subText: "Lifetime Access",
    color: "secondary",
    isExclusive: false,
    isPpv: false,
    isFree: false,
    isBuy: true,
    price,
    formattedPrice: `$${toFixed(price)}`,
    badgeClass:
      "bg-zinc-900/80 text-secondary border border-secondary/40 shadow-black/40",
    tagClass: "bg-secondary/10 text-secondary border border-secondary/20",
    btnText: `Buy Now ($${toFixed(price)})`,
  };
};

/**
 * Extracts and normalizes the active subscription from user data.
 * Supports:
 * 1. backend active_user_packages array (from POST /login)
 * 2. direct subscription/package properties (from POST /subscribe)
 */
export const getActiveUserSubscription = (userData) => {
  if (!userData) return null;

  // 1. Check active_user_packages array (from backend login response)
  const packagesList = Array.isArray(userData?.active_user_packages)
    ? userData.active_user_packages
    : [];

  const now = Date.now();

  const validActivePackage = packagesList.find((pkgItem) => {
    if (!pkgItem) return false;
    // Disregard cancelled subscriptions
    if (pkgItem.cancelled_at) return false;

    // Check expiration timestamp if provided
    if (pkgItem.expires_at) {
      // Normalize date string for reliable parsing across Safari and Chrome (e.g. replace space with 'T')
      const dateStr = String(pkgItem.expires_at).trim().replace(" ", "T");
      const expiryTime = new Date(dateStr).getTime();
      if (!isNaN(expiryTime) && expiryTime <= now) {
        return false; // Expired
      }
    }
    return true;
  });

  if (validActivePackage) {
    const pkgDetails = validActivePackage.package || {};
    return {
      id: validActivePackage.id || validActivePackage.user_package_id,
      user_package_id: validActivePackage.id || validActivePackage.user_package_id,
      package_id: validActivePackage.package_id || pkgDetails.id,
      title: pkgDetails.title || pkgDetails.name || "BAB Premium VIP",
      name: pkgDetails.title || pkgDetails.name || "BAB Premium VIP",
      description: pkgDetails.description || "",
      price: pkgDetails.price,
      duration: pkgDetails.duration,
      duration_type: pkgDetails.duration_type || "day",
      expires_at: validActivePackage.expires_at,
      cancelled_at: validActivePackage.cancelled_at || null,
      package: pkgDetails,
      raw: validActivePackage,
    };
  }

  // 2. Check direct properties (post-checkout / legacy state)
  if (
    userData?.is_subscribed ||
    userData?.subscription ||
    userData?.package_id ||
    userData?.package
  ) {
    const pkg = userData.subscription || userData.package || {};
    return {
      id: pkg.id || userData.package_id,
      user_package_id: userData.user_package_id || pkg.id,
      package_id: pkg.id || userData.package_id,
      title: pkg.title || pkg.name || "BAB Premium VIP",
      name: pkg.title || pkg.name || "BAB Premium VIP",
      description: pkg.description || "",
      price: pkg.price,
      duration: pkg.duration,
      duration_type: pkg.duration_type || "day",
      expires_at: userData.expires_at || pkg.expires_at,
      package: pkg,
      raw: pkg,
    };
  }

  return null;
};

/**
 * Checks whether the current user holds an active VIP subscription
 */
export const isUserSubscribed = (userData) => {
  if (!userData) return false;
  return Boolean(
    getActiveUserSubscription(userData) ||
    userData?.is_subscribed ||
    userData?.subscription_status === "active"
  );
};

/**
 * Checks whether an item can be consumed/accessed directly by the user
 */
export const isItemAccessible = (item, userData) => {
  if (!item) return false;
  if (item?.is_purchased) return true;
  const access = getItemAccessType(item);
  if (access.isFree) return true;
  if (access.isExclusive && isUserSubscribed(userData)) return true;
  return false;
};

